import { RebalanceType } from '../../types/rebalance'
import { convertEpochToDate, roundOffAndFormatNumberWithCurrency } from '../../utils/utils'
import Info from '../ui-components/Info'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import BottomSheetContent from '../BottomSheetComponents/BottomSheetContent'
import ELSSHelpContent from '../BottomSheetComponents/ELSSHelpContent'
export interface RebalanceHeaderProps {
  type?: RebalanceType
  currentWealth?: number
  highExposureTransferAmount?: number
  isElss?: boolean
  recommendationDate?: number
}

const ELSSInfo = () => {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const handleHelpClick = () => {
    openBottomSheet(
      <BottomSheetContent
        heading="Here's how to go about rebalancing for ELSS funds:"
        bottomSheetBodyClassName='!px-0'
        logoSrc='/images/power-rebalance/manual-book.png'
        onDismiss={closeBottomSheet}
      >
        <ELSSHelpContent onDismiss={closeBottomSheet} />
      </BottomSheetContent>
    )
  }

  return (
    <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
      You can only switch the units that are out of the 3-year lock-in period in ELSS funds.
      <span className='text-blue30 underline cursor-pointer block' onClick={handleHelpClick}>
        Know more on ELSS Rebalancing
      </span>
    </Info>
  )
}

export const RebalanceHeader = ({
  type,
  currentWealth = 0,
  highExposureTransferAmount = 0,
  recommendationDate,
  isElss = false
}: RebalanceHeaderProps) => {
  const renderContent = () => {
    switch (type) {
      // 1. Switch your ₹3,44,000 lump-sum
      case RebalanceType.LUMPSUM:
        return (
          <div className='text-xl'>
            <span className='text-grey40'>Switch your </span>
            <span className='text-black'>{roundOffAndFormatNumberWithCurrency(currentWealth)} lump-sum </span>
            <span className='text-grey40'>and </span>
            <span className='text-black'>current SIP (if any)</span>
            <span className='text-grey40'> to the new fund</span>
          </div>
        )

      // 2. Switch ₹3,44,000 lump-sum via SWP
      case RebalanceType.SWP:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Switch your </span>
              <span className='text-black'>
                {roundOffAndFormatNumberWithCurrency(currentWealth)} via equal instalments or SWP{' '}
              </span>
              <span className='text-grey40'>and </span>
              <span className='text-black'>current SIP (if any)</span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
              When moving large amounts, it's better to do it in instalments or SWP. This reduces the risk of wrong
              market timing.
            </Info>
          </>
        )

      // 3. Redirect your current SIP
      case RebalanceType.SIP:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Redirect your </span>
              <span className='text-black'>current SIP</span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
              But keep your current lump-sum of {roundOffAndFormatNumberWithCurrency(currentWealth)} invested.
            </Info>
          </>
        )

      // 5. Switch your ₹3,44,000 lump-sum and current SIP
      case RebalanceType.LUMPSUM_SIP:
        return (
          <div className='text-xl'>
            <span className='text-grey40'>Switch your </span>
            <span className='text-black'>
              {roundOffAndFormatNumberWithCurrency(currentWealth)} lump-sum and current SIP (if any)
            </span>
            <span className='text-grey40'> to the new fund</span>
          </div>
        )

      // 6. Switch your ₹3,44,000 lump-sum via SWP and your current SIP
      case RebalanceType.SWP_SIP:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Switch your </span>
              <span className='text-black'>
                {roundOffAndFormatNumberWithCurrency(currentWealth)} via equal instalments or SWP and your current SIP
                (if any)
              </span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
              When moving large amounts, it's better to do it in instalments or SWP. This reduces the risk of wrong
              market timing.
            </Info>
          </>
        )

      // 7. Switch your ₹1,72,000/₹3,56,889 lump-sum
      case RebalanceType.LUMPSUM_HIGH_EXPOSURE:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Switch your </span>
              <span className='text-black'>{roundOffAndFormatNumberWithCurrency(highExposureTransferAmount)}</span>
              <span className='text-grey40'> lump-sum</span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
              This recommendation aims to reduce high exposure (concentration risk) in a single fund, keeping your portfolio
              well-diversified.
            </Info>
          </>
        )

      // 8. Switch your ₹1,72,000/₹3,56,889 lump-sum via SWP
      case RebalanceType.SWP_HIGH_EXPOSURE:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Switch your </span>
              <span className='text-black'>{roundOffAndFormatNumberWithCurrency(highExposureTransferAmount)}</span>
              <span className='text-grey40'> via equal instalments or SWP</span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <div className='flex flex-col gap-2'>
              <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
                This recommendation aims to reduce high exposure (concentration risk) in a single fund, keeping your portfolio
                well-diversified.
              </Info>
              <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
                When moving large amounts, it's better to do it in instalments or SWP. This reduces the risk of wrong
                market timing.
              </Info>
            </div>
          </>
        )

      case RebalanceType.LUMPSUM_ELSS:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Switch your </span>
              <span className='text-black'>{roundOffAndFormatNumberWithCurrency(currentWealth)} lump-sum</span>
              <span className='text-grey40'> and </span>
              <span className='text-black'>current SIP (if any)</span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <div className='flex flex-col gap-2'>{isElss && <ELSSInfo />}</div>
          </>
        )
      case RebalanceType.LUMPSUM_SIP_ELSS:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Switch your </span>
              <span className='text-black'>{roundOffAndFormatNumberWithCurrency(currentWealth)} lump-sum</span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <div className='flex flex-col gap-2'>{isElss && <ELSSInfo />}</div>
          </>
        )
      case RebalanceType.SIP_ELSS:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Redirect your </span>
              <span className='text-black'>current SIP</span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <div className='flex flex-col gap-2'>
              <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
                But keep your current lump-sum of {roundOffAndFormatNumberWithCurrency(currentWealth)} invested.
              </Info>
              {isElss && <ELSSInfo />}
            </div>
          </>
        )
      case RebalanceType.SWP_ELSS:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Switch your </span>
              <span className='text-black'>
                {roundOffAndFormatNumberWithCurrency(currentWealth)} via equal instalments or SWP
              </span>
              <span className='text-grey40'>and </span>
              <span className='text-black'>current SIP (if any)</span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <div className='flex flex-col gap-2'>
              {isElss && <ELSSInfo />}
              <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
                When moving large amounts, it's better to do it in instalments or SWP. This reduces the risk of wrong
                market timing.
              </Info>
            </div>
          </>
        )
      case RebalanceType.SWP_SIP_ELSS:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Switch your </span>
              <span className='text-black'>
                {roundOffAndFormatNumberWithCurrency(currentWealth)} via equal instalments or SWP and your current SIP
              </span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <div className='flex flex-col gap-2'>
              <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
                When moving large amounts, it's better to do it in instalments or SWP. This reduces the risk of wrong
                market timing.
              </Info>
              {isElss && <ELSSInfo />}
            </div>
          </>
        )
      case RebalanceType.SWP_HIGH_EXPOSURE_ELSS:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Switch your </span>
              <span className='text-black'>{roundOffAndFormatNumberWithCurrency(highExposureTransferAmount)}</span>
              <span className='text-grey40'> via equal instalments or SWP</span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <div className='flex flex-col gap-2'>
              {isElss && <ELSSInfo />}
              <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
                This recommendation aims to reduce high exposure (concentration risk) in a single fund, keeping your portfolio
                well-diversified.
              </Info>
              <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
                When moving large amounts, it's better to do it in instalments or SWP. This reduces the risk of wrong
                market timing.
              </Info>
            </div>
          </>
        )
      case RebalanceType.LUMPSUM_HIGH_EXPOSURE_ELSS:
        return (
          <>
            <div className='text-xl'>
              <span className='text-grey40'>Switch your </span>
              <span className='text-black'>{roundOffAndFormatNumberWithCurrency(highExposureTransferAmount)}</span>
              <span className='text-grey40'> lump-sum</span>
              <span className='text-grey40'> to the new fund</span>
            </div>
            <div className='flex flex-col gap-2'>
              {isElss && <ELSSInfo />}
              <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
                This recommendation aims to reduce high exposure (concentration risk) in a single fund, keeping your portfolio
                well-diversified.
              </Info>
              <Info className='!gap-2' lineClassName='!w-[2px]' contentClassName='text-13 text-grey40'>
                When moving large amounts, it's better to do it in instalments or SWP. This reduces the risk of wrong
                market timing.
              </Info>
            </div>
          </>
        )
      default:
        return null
    }
  }

  return (
    <div className='px-5 flex flex-col mb-8'>
      {recommendationDate && (
        <div className='bg-status-on-track mb-6 rounded-xl'>
          <Info
            className='!gap-2 p-4'
            lineClassName='!w-[2px] bg-status-text-in-form'
            contentClassName='text-13 text-status-text-on-track'
          >
            <strong>Recommendation shown as of {convertEpochToDate(recommendationDate)}. </strong>
            <br />
            You've already withdrawn the suggested amount from this fund.
          </Info>
        </div>
      )}
      <div className='text-orange30 text-13 font-medium mb-4'>Rebalance recommendation</div>
      {renderContent()}
    </div>
  )
}

RebalanceHeader.defaultProps = {
  type: RebalanceType.SWP_HIGH_EXPOSURE,
  currentWealth: 0,
  highExposureTransferAmount: 0
}
